import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth, formatApiErrorDetail } from "../lib/auth";
import { ArrowRight, Loader2 } from "lucide-react";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_91fc1071-a755-4ee2-8eeb-0c8b2e7c23e2/artifacts/frr6xg90_Product%20Icon%401x.png";

const LEVELS = [
  { code: "A1", label: "Beginner", desc: "Starting from zero" },
  { code: "A2", label: "Elementary", desc: "Basic phrases" },
  { code: "B1", label: "Intermediate", desc: "Simple conversations" },
  { code: "B2", label: "Upper-Int.", desc: "Daily comfort" },
  { code: "C1", label: "Advanced", desc: "Very strong" },
  { code: "C2", label: "Mastery", desc: "Near-native" },
];

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [level, setLevel] = useState("A1");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await register(email, password, name, level);
      navigate("/app");
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (detail) {
        setError(formatApiErrorDetail(detail));
      } else if (err.message) {
        setError(`Network error: ${err.message}. Please check your connection and try again.`);
      } else {
        setError("Something went wrong. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-navy-900 text-white grain flex">
      <div className="hidden lg:flex flex-col justify-between flex-1 p-12 bg-navy-800 border-r border-white/5">
        <Link to="/" className="flex items-center gap-3">
          <img src={LOGO_URL} alt="Avoir la pêche" className="h-12 w-12 rounded-2xl border border-white/10" />
          <div className="font-heading font-bold text-lg">Avoir la pêche</div>
        </Link>
        <div>
          <div className="text-xs tracking-[0.3em] uppercase text-peach-500 font-bold mb-4">Create your account</div>
          <h2 className="font-heading font-bold text-4xl leading-tight tracking-tight max-w-md">
            You're 30 seconds away<br />from your first <span className="gradient-text">"Bonjour"</span>.
          </h2>
          <p className="mt-6 text-[#A89BC9] max-w-md leading-relaxed">
            Pick your level and the AI will adapt to you. You can change it anytime.
          </p>
        </div>
        <div className="text-xs text-[#A89BC9]">© {new Date().getFullYear()} Avoir la pêche</div>
      </div>

      <div className="flex-1 flex items-center justify-center p-6 md:p-12 overflow-y-auto">
        <div className="w-full max-w-md">
          <Link to="/" className="lg:hidden flex items-center gap-3 mb-8">
            <img src={LOGO_URL} alt="Avoir la pêche" className="h-10 w-10 rounded-xl" />
            <div className="font-heading font-bold">Avoir la pêche</div>
          </Link>
          <h1 className="font-heading font-bold text-3xl mb-2">Create an account</h1>
          <p className="text-[#A89BC9] text-sm mb-8">Free, no credit card required.</p>

          <form onSubmit={onSubmit} className="space-y-4">
            <div>
              <label className="text-xs uppercase tracking-[0.2em] text-[#A89BC9] font-semibold">First name</label>
              <input
                type="text"
                required
                value={name}
                onChange={(e) => setName(e.target.value)}
                data-testid="register-name-input"
                className="mt-2 w-full bg-white/5 border border-white/10 rounded-2xl px-4 py-3.5 text-white placeholder:text-[#A89BC9]/50 focus:border-peach-500 focus:ring-1 focus:ring-peach-500 outline-none transition"
                placeholder="Marie"
              />
            </div>
            <div>
              <label className="text-xs uppercase tracking-[0.2em] text-[#A89BC9] font-semibold">Email</label>
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="register-email-input"
                className="mt-2 w-full bg-white/5 border border-white/10 rounded-2xl px-4 py-3.5 text-white placeholder:text-[#A89BC9]/50 focus:border-peach-500 focus:ring-1 focus:ring-peach-500 outline-none transition"
                placeholder="you@example.com"
              />
            </div>
            <div>
              <label className="text-xs uppercase tracking-[0.2em] text-[#A89BC9] font-semibold">Password</label>
              <input
                type="password"
                required
                minLength={6}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                data-testid="register-password-input"
                className="mt-2 w-full bg-white/5 border border-white/10 rounded-2xl px-4 py-3.5 text-white placeholder:text-[#A89BC9]/50 focus:border-peach-500 focus:ring-1 focus:ring-peach-500 outline-none transition"
                placeholder="At least 6 characters"
              />
            </div>
            <div>
              <label className="text-xs uppercase tracking-[0.2em] text-[#A89BC9] font-semibold">Your level</label>
              <div className="mt-2 grid grid-cols-3 gap-2" data-testid="register-level-grid">
                {LEVELS.map((lv) => (
                  <button
                    key={lv.code}
                    type="button"
                    onClick={() => setLevel(lv.code)}
                    data-testid={`register-level-${lv.code}`}
                    className={`px-3 py-3 rounded-2xl text-left transition border ${
                      level === lv.code
                        ? "bg-peach-500/15 border-peach-500/50 text-peach-200"
                        : "bg-white/5 border-white/10 text-white hover:border-white/30"
                    }`}
                  >
                    <div className="font-heading font-bold text-sm">{lv.code}</div>
                    <div className="text-[11px] text-[#A89BC9] mt-0.5 leading-tight">{lv.label}</div>
                  </button>
                ))}
              </div>
            </div>

            {error && (
              <div data-testid="register-error" className="text-sm text-red-300 bg-red-500/10 border border-red-500/20 rounded-2xl px-4 py-3">
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              data-testid="register-submit-btn"
              className="w-full bg-peach-500 text-navy-900 font-semibold py-3.5 rounded-full hover:bg-peach-400 transition peach-glow flex items-center justify-center gap-2 disabled:opacity-60"
            >
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Create my account <ArrowRight className="h-4 w-4" /></>}
            </button>
          </form>

          <div className="mt-8 text-sm text-[#A89BC9] text-center">
            Already have an account?{" "}
            <Link to="/login" data-testid="register-to-login-link" className="text-peach-300 hover:text-peach-400 font-semibold">
              Sign in
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
